import {
  Alert,
  Button,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatNumber } from 'pages/star-shared/utils';
import UserAvatar from 'components/user-avatar';
import { colors } from 'theme/tokens';
import { useDataImport } from '../context';
import {
  ImportKind,
  ImportNotice,
  ImportResult,
  MatchAction,
  PlanItem,
  Reconcile,
  ReconcileRow,
  RowError,
} from '../types';
import {
  ACTION_META,
  dataImportLink,
  formatDay,
  formatLiveClock,
  formatLiveHours,
  formatMonth,
  formatPeriod,
} from '../utils';
import styles from '../style.module.scss';

type Filter = 'attention' | 'all' | 'errors';

interface Props {
  result: ImportResult;
  kind: ImportKind;
  /** Đang gửi lệnh áp dụng. */
  applying: boolean;
  /** `overrideReason`: Admin nhập file tháng khi tháng còn thiếu ngày (kèm lý do). */
  onApply: (overrideReason?: string) => void;
  onReset: () => void;
  /** Chỉ xem (hộp Chi tiết của nhập hàng loạt): ẩn nút Áp dụng / Chọn file khác. */
  readOnly?: boolean;
}

/** Lý do bỏ qua phải đủ dài để đọc lại được ở Lịch sử nhập (khớp OVERRIDE_REASON_MIN máy chủ). */
const OVERRIDE_REASON_MIN = 10;

/** Nút gợi ý của một dòng chặn / cảnh báo: mở tab (và tháng) đúng với file. */
const noticeAction = (notice: ImportNotice) => {
  if (!notice.action) return undefined;
  const { tab, month } = notice.action;
  const label = `Mở Data ${tab === 'ngay' ? 'ngày' : 'tháng'}${
    month ? ` ${formatMonth(month)}` : ''
  }`;
  return (
    <Link to={dataImportLink(tab, month)}>
      <Button size="small">{label}</Button>
    </Link>
  );
};

const signed = (value: number) => `${value > 0 ? '+' : ''}${formatNumber(value)}`;

const reconcileColumns: ColumnsType<ReconcileRow> = [
  {
    title: 'TikTok ID · UID',
    key: 'who',
    render: (_, row) => (
      <div className={styles.wrapAnywhere}>
        {row.username ? (
          `@${row.username}`
        ) : (
          <Typography.Text type="secondary">(trống)</Typography.Text>
        )}
        <div className={styles.rowNote}>{row.uid}</div>
      </div>
    ),
  },
  {
    title: 'File tháng',
    dataIndex: 'fileDiamonds',
    align: 'right',
    width: 120,
    render: (value: number) => formatNumber(value || 0),
  },
  {
    title: 'Cộng các ngày',
    dataIndex: 'daysDiamonds',
    align: 'right',
    width: 130,
    render: (value: number) => formatNumber(value || 0),
  },
  {
    title: 'Lệch',
    dataIndex: 'diff',
    align: 'right',
    width: 110,
    render: (value: number) => signed(value || 0),
  },
];

// Ghi chú của máy chủ (70–90 ký tự) nằm ngay dưới tài khoản app, không tách cột riêng: cột
// "Ghi chú" riêng từng bị co còn ~60px (tiêu đề gãy hai dòng), còn cho đủ rộng thì ở màn 1440
// phải cuộn ngang mới đọc được. Tổng bề rộng các cột vừa khung nội dung của màn 1440.
const columns: ColumnsType<PlanItem> = [
  {
    title: 'Kết quả',
    dataIndex: 'action',
    width: 150,
    render: (action: MatchAction) => {
      const meta = ACTION_META[action] || ACTION_META.UNMATCHED;
      return (
        <Tooltip title={meta.help}>
          <Tag color={meta.color}>{meta.label}</Tag>
        </Tooltip>
      );
    },
  },
  {
    title: 'UID TikTok (cột B)',
    dataIndex: 'uid',
    width: 190,
    render: (uid: string) => (
      <Typography.Text copyable className={styles.uid}>
        {uid}
      </Typography.Text>
    ),
  },
  {
    title: 'TikTok ID (cột C)',
    dataIndex: 'fileUsername',
    width: 170,
    render: (name: string | null) =>
      name ? (
        <span className={styles.wrapAnywhere}>@{name}</span>
      ) : (
        <Typography.Text type="secondary">(trống)</Typography.Text>
      ),
  },
  {
    title: 'Tài khoản app · Ghi chú',
    key: 'user',
    width: 300,
    render: (_, row) => (
      <div className={styles.wrapAnywhere}>
        {row.user ? (
          <Space align="start">
            <UserAvatar size="small" src={row.user.avatar} />
            <span>
              #{row.user.id} {row.user.nickName}{' '}
              <Typography.Text type="secondary">@{row.user.username}</Typography.Text>
            </span>
          </Space>
        ) : (
          !row.note && <Typography.Text type="secondary">—</Typography.Text>
        )}
        {row.note && <div className={styles.rowNote}>{row.note}</div>}
      </div>
    ),
  },
  {
    title: 'Kim cương',
    dataIndex: 'diamonds',
    align: 'right',
    width: 115,
    sorter: (a, b) => a.diamonds - b.diamonds,
    render: (value: number) => formatNumber(value || 0),
  },
  {
    title: 'LIVE',
    key: 'live',
    width: 145,
    render: (_, row) => `${row.liveDays} ngày · ${formatLiveClock(row.liveSeconds)}`,
  },
];

const columnsWidth = columns.reduce((sum, column) => sum + (Number(column.width) || 0), 0);

const errorColumns: ColumnsType<RowError> = [
  { title: 'Dòng', dataIndex: 'line', width: 80 },
  { title: 'UID', dataIndex: 'uid', width: 210 },
  { title: 'TikTok ID', dataIndex: 'username', width: 200 },
  { title: 'Lý do', dataIndex: 'reason' },
];

interface StatProps {
  title: string;
  value: string;
  hint?: string;
  danger?: boolean;
  /** Chiếm hai ô của lưới (giá trị dài). */
  wide?: boolean;
}

// Giá trị đã định dạng sẵn kiểu vi-VN ("1.234"): không để Statistic tự tách phần thập phân.
const asIs = (value: string | number) => value;

const Stat = ({ title, value, hint, danger, wide }: StatProps) => (
  <div className={wide ? `${styles.stat} ${styles.statWide}` : styles.stat}>
    <Statistic
      title={title}
      value={value}
      formatter={asIs}
      valueStyle={danger ? { fontSize: 20, color: colors.dangerInk } : { fontSize: 20 }}
    />
    {hint && <div className={styles.statHint}>{hint}</div>}
  </div>
);

/** Đối chiếu file tháng với số đã nhập theo ngày của cùng tháng. */
const ReconcileBlock = ({ data }: { data: Reconcile }) => {
  const diff = data.fileDiamonds - data.daysDiamonds;
  const lech = data.creators.mismatched + data.creators.onlyFile + data.creators.onlyDays;
  return (
    <div className={styles.reconcile}>
      <h3 className={styles.headTitle}>
        Đối chiếu với dữ liệu ngày của tháng {formatMonth(data.month)}
      </h3>
      <div className={styles.statGrid}>
        <Stat title="Tổng file tháng" value={formatNumber(data.fileDiamonds)} />
        <Stat title="Cộng các ngày" value={formatNumber(data.daysDiamonds)} />
        <Stat danger={diff !== 0} title="Lệch (KC)" value={signed(diff)} />
        <Stat
          danger={lech > 0}
          title="Creator có số khác"
          value={formatNumber(lech)}
          hint={
            lech > 0
              ? `${formatNumber(data.creators.mismatched)} khác số · ${formatNumber(
                  data.creators.onlyFile
                )} chỉ có ở file tháng · ${formatNumber(data.creators.onlyDays)} chỉ có ở file ngày`
              : undefined
          }
        />
      </div>
      {data.top.length > 0 ? (
        <Table
          size="small"
          rowKey="uid"
          dataSource={data.top}
          columns={reconcileColumns}
          pagination={false}
          scroll={{ x: 560 }}
        />
      ) : (
        <Alert type="success" showIcon message="Số của file tháng khớp với cộng các ngày." />
      )}
    </div>
  );
};

/**
 * Kết quả xem trước / áp dụng một file, hiện ngay trong trang (không phải modal):
 * khối chặn (đỏ), cảnh báo (vàng), số liệu tóm tắt, bảng ghép UID như modal nhập cũ.
 * Nút "Áp dụng N dòng" bị khoá khi còn khối chặn.
 */
const ImportResultPanel = ({ result, kind, applying, onApply, onReset, readOnly }: Props) => {
  const { isAdmin } = useDataImport();
  const summary = result.summary;
  const [filter, setFilter] = useState<Filter>(summary.errors ? 'errors' : 'attention');
  const [reason, setReason] = useState('');

  const items = useMemo(
    () => (filter === 'all' ? result.items : result.items.filter((item) => item.action !== 'OK')),
    [result.items, filter]
  );

  const matched = summary.OK + summary.RENAME + summary.RENAME_SKIPPED + summary.RENAME_CONFLICT;
  const conflicts = summary.RENAME_CONFLICT + summary.LINK_CONFLICT;
  const periodText = result.periods.length
    ? result.periods.map((period) => formatPeriod(period.start, period.end)).join(', ')
    : '—';
  // "01/08/2026 → 31/08/2026" không vừa một ô số liệu ở màn hẹp → cho chiếm hai ô.
  const widePeriod = kind === 'MONTHLY' || result.periods.length > 1;
  const blocked = result.blockers.length > 0;
  // Mọi điều chặn đều cho Admin bỏ qua kèm lý do (tháng đang nhập theo ngày còn thiếu ngày).
  const canOverride =
    !result.applied && blocked && isAdmin && result.blockers.every((b) => b.overridable);
  const overrideReady = canOverride && reason.trim().length >= OVERRIDE_REASON_MIN;
  // Tháng đã đủ ngày mà vẫn nhập file tháng: tổng tháng đổi sang số của file tháng.
  const overDays = result.warnings.some((w) => w.code === 'MONTHLY_OVER_DAYS');
  // Ghi đè số đã có: hỏi lại một lần trước khi áp dụng.
  const overwrite =
    !!result.existing ||
    overDays ||
    result.warnings.some((w) => w.code === 'ALREADY_IMPORTED' || w.code === 'MANUAL_OVERWRITE');
  const star = result.starEvaluation;
  const confirmText = overrideReady
    ? {
        title: 'Nhập file tháng khi còn thiếu ngày?',
        description:
          'Tổng tháng sẽ lấy theo file tháng; các ngày thiếu vẫn không có dữ liệu ngày cho Điểm Sao và phân tích. Lý do được lưu ở Lịch sử nhập.',
        okText: 'Vẫn nhập',
      }
    : overDays && !result.existing
    ? {
        title: 'Lấy file tháng làm tổng tháng?',
        description:
          'Tháng này đã đủ ngày theo file ngày. Áp dụng sẽ lấy số của file tháng làm tổng tháng; số từng ngày vẫn giữ.',
        okText: 'Áp dụng',
      }
    : {
        title: 'Ghi đè số liệu đã có?',
        description: 'Giai đoạn này đã có số liệu. Áp dụng sẽ thay số cũ bằng số trong file.',
        okText: 'Ghi đè',
      };
  const confirm = overrideReady || (overwrite && !blocked && !!summary.rows);
  const apply = () => onApply(overrideReady ? reason.trim() : undefined);

  const applyButton = (
    <Button
      type="primary"
      loading={applying}
      disabled={(blocked && !overrideReady) || !summary.rows}
      onClick={confirm ? undefined : apply}
    >
      {overrideReady ? 'Vẫn nhập file tháng' : 'Áp dụng'} {formatNumber(summary.rows)} dòng
    </Button>
  );

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.fileName}>{result.fileName}</span>
        {result.existing && !result.applied && (
          <Typography.Text type="secondary" className={styles.small}>
            Đã nhập lúc {formatDateTime(result.existing.importedAt)}
            {result.existing.importedByName ? ` bởi ${result.existing.importedByName}` : ''} (
            {formatNumber(result.existing.rows)} dòng, {formatNumber(result.existing.diamonds)} KC)
          </Typography.Text>
        )}
      </div>

      {result.applied && (
        <div className={styles.notices}>
          <Alert
            type="success"
            showIcon
            message={`Đã lưu ${formatNumber(summary.statsSaved ?? summary.rows)} dòng số liệu`}
            description={
              <ul className={styles.noticeList}>
                <li>
                  {kind === 'DAILY' ? 'Ngày' : 'Giai đoạn'}: {periodText}
                </li>
                <li>
                  Gắn UID mới cho {formatNumber(summary.LINK)} tài khoản. Không sửa thông tin tài
                  khoản.
                </li>
                {result.departed.rows > 0 && (
                  <li>
                    Bỏ qua {formatNumber(result.departed.rows)} Creator đã rời đại lý (
                    {formatNumber(result.departed.diamonds)} KC).
                  </li>
                )}
                {kind === 'DAILY' && star?.ok && (
                  <li>
                    Đã chấm Sao: {formatNumber(star.rewarded)} Creator, +{formatNumber(star.stars)}{' '}
                    ⭐ (đã xét {formatNumber(star.evaluated)}, bỏ qua {formatNumber(star.skipped)})
                  </li>
                )}
                {kind === 'DAILY' && star?.ok && !!star.monthly?.rewarded && (
                  <li>
                    Sao đều tay của tháng: {formatNumber(star.monthly.rewarded)} Creator, +
                    {formatNumber(star.monthly.stars)} ⭐
                  </li>
                )}
                <li>
                  {result.logId ? `Mã lần nhập #${result.logId}. ` : ''}
                  <Link to="/data-import?tab=lich-su">Xem lịch sử nhập</Link>
                </li>
              </ul>
            }
          />
          {kind === 'DAILY' && !star?.ok && (
            <Alert
              type="warning"
              showIcon
              message={star ? 'Chưa chấm được Sao' : 'Máy chủ chưa trả kết quả chấm Sao'}
              description={
                star?.message ||
                'Số liệu đã lưu. Vào Doanh thu → Data Kim cương → Chấm lại khoảng ngày để chấm lại ngày này.'
              }
            />
          )}
        </div>
      )}

      {!result.applied && blocked && (
        <div className={styles.notices}>
          {result.blockers.map((blocker, index) => (
            <Alert
              key={`${blocker.code}-${index}`}
              type="error"
              showIcon
              message={blocker.message}
              action={noticeAction(blocker)}
            />
          ))}
          {canOverride && (
            <div className={styles.overrideBox}>
              <label htmlFor="data-import-override" className={styles.overrideLabel}>
                Admin: lý do vẫn nhập file tháng (ít nhất {OVERRIDE_REASON_MIN} ký tự, lưu ở Lịch sử
                nhập)
              </label>
              <Input.TextArea
                id="data-import-override"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={200}
                showCount
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder="Ví dụ: TikTok LIVE Backstage không còn xuất được ngày 30/08 và 31/08"
                disabled={applying}
              />
            </div>
          )}
        </div>
      )}

      {!result.applied && result.warnings.length > 0 && (
        <div className={styles.notices}>
          {result.warnings.map((warning, index) => (
            <Alert
              key={`${warning.code}-${index}`}
              type="warning"
              showIcon
              message={warning.message}
              action={noticeAction(warning)}
            />
          ))}
        </div>
      )}

      <div className={styles.statGrid}>
        <Stat
          wide={widePeriod}
          title={kind === 'DAILY' ? 'Ngày của file' : 'Giai đoạn của file'}
          value={kind === 'DAILY' && result.day ? formatDay(result.day) : periodText}
        />
        <Stat title="Creator" value={formatNumber(summary.creators)} />
        <Stat title="Kim cương" value={formatNumber(result.totals.diamonds)} />
        <Stat title="Giờ LIVE" value={formatLiveHours(result.totals.liveSeconds)} />
        <Stat title="Đã khớp UID" value={formatNumber(matched)} />
        <Stat title="Gắn UID mới" value={formatNumber(summary.LINK)} />
        <Stat title="Chưa có tài khoản" value={formatNumber(summary.UNMATCHED)} />
        <Stat
          title="Đã rời đại lý"
          value={formatNumber(result.departed.rows)}
          hint={
            result.departed.rows > 0 ? `${formatNumber(result.departed.diamonds)} KC` : undefined
          }
        />
        {conflicts > 0 && <Stat danger title="Cần xử lý" value={formatNumber(conflicts)} />}
        <Stat danger={summary.errors > 0} title="Dòng lỗi" value={formatNumber(summary.errors)} />
      </div>

      {!result.applied && result.reconcile && <ReconcileBlock data={result.reconcile} />}

      {!readOnly && (
        <div className={styles.actions}>
          <Button onClick={onReset} disabled={applying}>
            {result.applied ? 'Nhập file khác' : 'Chọn file khác'}
          </Button>
          {!result.applied &&
            (confirm ? (
              <Popconfirm
                title={confirmText.title}
                description={confirmText.description}
                okText={confirmText.okText}
                cancelText="Huỷ"
                // Nút nằm sát mép phải: canh hộp hỏi theo mép phải của nút để không dính mép màn
                // hình; giới hạn bề rộng để ở điện thoại hộp hỏi không tràn ra ngoài mép trái.
                placement="topRight"
                styles={{ root: { maxWidth: 'min(340px, calc(100vw - 48px))' } }}
                onConfirm={apply}
                disabled={applying}
              >
                {applyButton}
              </Popconfirm>
            ) : (
              applyButton
            ))}
        </div>
      )}

      <div className={styles.filter}>
        <Segmented
          value={filter}
          onChange={(value) => setFilter(value as Filter)}
          options={[
            {
              label: `Cần xem (${formatNumber(summary.creators - summary.OK)})`,
              value: 'attention',
            },
            { label: `Tất cả (${formatNumber(summary.creators)})`, value: 'all' },
            { label: `Dòng lỗi (${formatNumber(summary.errors)})`, value: 'errors' },
          ]}
        />
      </div>

      {filter === 'errors' ? (
        <Table
          size="small"
          rowKey={(row) => `${row.line}-${row.uid}`}
          dataSource={result.errors}
          columns={errorColumns}
          scroll={{ x: 760 }}
          pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{ emptyText: 'File không có dòng lỗi nào' }}
        />
      ) : (
        <Table
          size="small"
          rowKey="uid"
          dataSource={items}
          columns={columns}
          scroll={{ x: columnsWidth }}
          pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
          locale={{
            emptyText:
              filter === 'attention' && summary.creators > 0
                ? 'Tất cả Creator đã khớp UID, không có gì cần xử lý'
                : 'Không có dòng nào',
          }}
        />
      )}
    </div>
  );
};

export default ImportResultPanel;
